import type {
  JiraTemplateFormValues,
  OnDemandField
} from "@/organisms/Ticketing/components/JiraTemplateModal"

/**
 * Transform workflow payload format to form values format
 * Main transformation: Extract IDs/keys from object format fields
 *
 * @param existingTemplate - The template data from workflow action
 * @returns Form values ready to be set via form.setFieldsValue()
 */
export function transformWorkflowPayloadToFormValues(existingTemplate: {
  fields: {
    summary: string
    project: { id: string }
    issuetype: { id: string }
    labels?: string[]
    description?: string
    [key: string]: any
  }
  onDemandFields?: {
    [fieldName: string]: OnDemandField
  }
}): JiraTemplateFormValues {
  const { fields, onDemandFields } = existingTemplate

  // Extract project and issuetype separately (always required)
  const { project, issuetype, ...otherFields } = fields

  // Transform all fields - handle both object format and primitive values
  const transformedFields: Record<string, any> = {}

  Object.entries(otherFields).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      // Skip null/undefined values
      return
    }

    if (Array.isArray(value)) {
      // Handle arrays (e.g., components, labels)
      if (value.length > 0 && typeof value[0] === "object" && value[0].id) {
        // Array of objects with id - extract ids
        transformedFields[key] = value.map((item) => item.id)
      } else {
        // Array of primitives or empty - keep as is
        transformedFields[key] = value
      }
    } else if (typeof value === "object" && value.id !== undefined) {
      // Single object with id property (e.g., assignee, priority, parent)
      transformedFields[key] = value.id
    } else if (typeof value === "object" && value.key !== undefined) {
      // Single object with key property (e.g., parent with key like "TJP-48")
      transformedFields[key] = value.key
    } else {
      // Primitive value or other format - keep as is
      transformedFields[key] = value
    }
  })

  return {
    // Core fields - extract ID from objects
    project: project.id || project,
    issuetype: issuetype.id || issuetype,

    // Spread all transformed fields
    ...transformedFields,

    // OnDemandFields already in correct format
    onDemandFields: onDemandFields || {},

    // Ensure reporter exists (required by form type)
    reporter: transformedFields.reporter || ""
  } as JiraTemplateFormValues
}
